import { useCallback, useMemo, useRef, useState } from "react";
import { GetMasterDataTenantLookupsUseCase } from "@src/application/useCases/masterData";
import type {
  AdjustmentCategoryLookupItem,
  DeductionPriorityLookupItem,
  DeductionStageLookupItem,
  DeductionValidityLookupItem,
  DeductionValidityRcaLookupItem,
} from "@src/domain/dto/masterData";
import { MasterDataTenantLookupsRepository } from "@src/infrastructure/repositories/masterData";

const tenantLookups = new GetMasterDataTenantLookupsUseCase(
  new MasterDataTenantLookupsRepository(),
);

interface BrandScopedLookupSlice<TItem> {
  items: TItem[] | null;
  count: number;
  loading: boolean;
  error: string | null;
  refetch: (brandKey: string | null) => Promise<void>;
}

function useBrandScopedLookup<TItem>(
  load: (brandKey: string) => Promise<{ items: TItem[]; count: number }>,
  fallbackMessage: string,
): BrandScopedLookupSlice<TItem> {
  const requestId = useRef(0);
  const [items, setItems] = useState<TItem[] | null>(null);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(
    async (brandKey: string | null) => {
      const id = ++requestId.current;
      const key = brandKey?.trim() || null;
      if (!key) {
        setItems(null);
        setCount(0);
        setError(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const result = await load(key);
        if (id !== requestId.current) {
          return;
        }
        setItems(result.items);
        setCount(result.count);
      } catch (err) {
        if (id !== requestId.current) {
          return;
        }
        setItems(null);
        setCount(0);
        setError(err instanceof Error ? err.message : fallbackMessage);
      } finally {
        if (id === requestId.current) {
          setLoading(false);
        }
      }
    },
    [fallbackMessage, load],
  );

  return useMemo(
    () => ({ items, count, loading, error, refetch }),
    [items, count, loading, error, refetch],
  );
}

export interface AdjustmentCategoriesLookupSlice {
  masterDataAdjustmentCategoriesLookup: AdjustmentCategoryLookupItem[] | null;
  masterDataAdjustmentCategoriesLookupCount: number;
  masterDataAdjustmentCategoriesLookupLoading: boolean;
  masterDataAdjustmentCategoriesLookupError: string | null;
  refetchMasterDataAdjustmentCategoriesLookup: (brandKey: string | null) => Promise<void>;
}

export function useAdjustmentCategoriesLookup(): AdjustmentCategoriesLookupSlice {
  const load = useCallback(
    (brandKey: string) => tenantLookups.getAdjustmentCategories(brandKey),
    [],
  );
  const slice = useBrandScopedLookup(load, "Failed to load adjustment categories");
  return useMemo(
    () => ({
      masterDataAdjustmentCategoriesLookup: slice.items,
      masterDataAdjustmentCategoriesLookupCount: slice.count,
      masterDataAdjustmentCategoriesLookupLoading: slice.loading,
      masterDataAdjustmentCategoriesLookupError: slice.error,
      refetchMasterDataAdjustmentCategoriesLookup: slice.refetch,
    }),
    [slice],
  );
}

export interface DeductionPrioritiesLookupSlice {
  masterDataDeductionPrioritiesLookup: DeductionPriorityLookupItem[] | null;
  masterDataDeductionPrioritiesLookupCount: number;
  masterDataDeductionPrioritiesLookupLoading: boolean;
  masterDataDeductionPrioritiesLookupError: string | null;
  refetchMasterDataDeductionPrioritiesLookup: (brandKey: string | null) => Promise<void>;
}

export function useDeductionPrioritiesLookup(): DeductionPrioritiesLookupSlice {
  const load = useCallback(
    (brandKey: string) => tenantLookups.getDeductionPriorities(brandKey),
    [],
  );
  const slice = useBrandScopedLookup(load, "Failed to load deduction priorities");
  return useMemo(
    () => ({
      masterDataDeductionPrioritiesLookup: slice.items,
      masterDataDeductionPrioritiesLookupCount: slice.count,
      masterDataDeductionPrioritiesLookupLoading: slice.loading,
      masterDataDeductionPrioritiesLookupError: slice.error,
      refetchMasterDataDeductionPrioritiesLookup: slice.refetch,
    }),
    [slice],
  );
}

export interface DeductionStagesLookupSlice {
  masterDataDeductionStagesLookup: DeductionStageLookupItem[] | null;
  masterDataDeductionStagesLookupCount: number;
  masterDataDeductionStagesLookupLoading: boolean;
  masterDataDeductionStagesLookupError: string | null;
  refetchMasterDataDeductionStagesLookup: (brandKey: string | null) => Promise<void>;
}

export function useDeductionStagesLookup(): DeductionStagesLookupSlice {
  const load = useCallback((brandKey: string) => tenantLookups.getDeductionStages(brandKey), []);
  const slice = useBrandScopedLookup(load, "Failed to load deduction stages");
  return useMemo(
    () => ({
      masterDataDeductionStagesLookup: slice.items,
      masterDataDeductionStagesLookupCount: slice.count,
      masterDataDeductionStagesLookupLoading: slice.loading,
      masterDataDeductionStagesLookupError: slice.error,
      refetchMasterDataDeductionStagesLookup: slice.refetch,
    }),
    [slice],
  );
}

export interface DeductionValiditiesLookupSlice {
  masterDataDeductionValiditiesLookup: DeductionValidityLookupItem[] | null;
  masterDataDeductionValiditiesLookupCount: number;
  masterDataDeductionValiditiesLookupLoading: boolean;
  masterDataDeductionValiditiesLookupError: string | null;
  refetchMasterDataDeductionValiditiesLookup: (brandKey: string | null) => Promise<void>;
}

export function useDeductionValiditiesLookup(): DeductionValiditiesLookupSlice {
  const load = useCallback(
    (brandKey: string) => tenantLookups.getDeductionValidities(brandKey),
    [],
  );
  const slice = useBrandScopedLookup(load, "Failed to load deduction validities");
  return useMemo(
    () => ({
      masterDataDeductionValiditiesLookup: slice.items,
      masterDataDeductionValiditiesLookupCount: slice.count,
      masterDataDeductionValiditiesLookupLoading: slice.loading,
      masterDataDeductionValiditiesLookupError: slice.error,
      refetchMasterDataDeductionValiditiesLookup: slice.refetch,
    }),
    [slice],
  );
}

export interface DeductionValidityRcasLookupSlice {
  masterDataDeductionValidityRcasLookup: DeductionValidityRcaLookupItem[] | null;
  masterDataDeductionValidityRcasLookupCount: number;
  masterDataDeductionValidityRcasLookupLoading: boolean;
  masterDataDeductionValidityRcasLookupError: string | null;
  refetchMasterDataDeductionValidityRcasLookup: (brandKey: string | null) => Promise<void>;
}

export function useDeductionValidityRcasLookup(): DeductionValidityRcasLookupSlice {
  const load = useCallback(
    (brandKey: string) => tenantLookups.getDeductionValidityRcas(brandKey),
    [],
  );
  const slice = useBrandScopedLookup(load, "Failed to load deduction validity RCA rows");
  return useMemo(
    () => ({
      masterDataDeductionValidityRcasLookup: slice.items,
      masterDataDeductionValidityRcasLookupCount: slice.count,
      masterDataDeductionValidityRcasLookupLoading: slice.loading,
      masterDataDeductionValidityRcasLookupError: slice.error,
      refetchMasterDataDeductionValidityRcasLookup: slice.refetch,
    }),
    [slice],
  );
}
